import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import { pricingFactors } from "@/config/site";
import { SEO, breadcrumbLd } from "@/lib/seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTA } from "@/components/CTA";

export default function Pricing() {
  return (
    <>
      <SEO
        title="Pricing & Estimate Expectations | North Ember Painting — Delta, BC"
        description="What affects the cost of painting in Delta, BC: surface area, condition, preparation, materials, access and more — and how our written estimates work."
        path="/pricing"
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Pricing & Estimates", path: "/pricing" }])]}
      />
      <PageHero
        eyebrow="Pricing & estimates"
        title="Honest pricing starts with understanding your project"
        intro="We don't publish one-size-fits-all rates, because no two homes — or paint jobs — are the same. Here's what actually shapes the cost, and how our estimates work."
        image="swatchCompare"
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-3xl rounded-2xl border border-amber-200/70 bg-amber-50/60 p-6">
              <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900">
                <Info className="h-5 w-5 text-amber-600" aria-hidden="true" /> Why we quote in writing, not off a price list
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Online price lists for painting are almost always misleading — they assume surfaces in perfect
                condition and simple access. We'd rather look at your actual project and give you a written
                estimate you can hold us to. Every estimate from North Ember Painting is free and comes with no
                obligation.
              </p>
            </div>
          </Reveal>

          <h2 className="mt-16 font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
            What affects the cost of a painting project
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pricingFactors.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 60}>
                <div className="tilt-card h-full rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <span className="font-display text-3xl font-extrabold text-ember/25">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-slate-900">{f.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 sm:p-10">
              <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
                How to get your number
              </h2>
              <ol className="mt-5 list-decimal space-y-3 pl-5 text-base leading-relaxed text-slate-600">
                <li>Send the estimate form with a few details about your project — photos help.</li>
                <li>We review it and contact you to discuss scope, and visit on-site when the project needs it.</li>
                <li>You receive a clear written estimate covering preparation, scope, materials approach and schedule.</li>
              </ol>
              <Link
                to="/estimate"
                data-testid="pricing-estimate-btn"
                className="mt-7 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-ember px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-600/25 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-ember-dark"
              >
                Request a Free Estimate <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
      <CTA />
    </>
  );
}
